import { z } from "zod";
import {
  type InvestingPrimaryGoal,
  type InvestingRiskTolerance,
} from "./investing-profile";

export const USERNAME_MIN_LENGTH = 3;
export const USERNAME_MAX_LENGTH = 20;

const reservedUsernames = [
  "glider",
  "admin",
  "administrator",
  "support",
  "help",
  "official",
  "staff",
  "team",
  "moderator",
  "security",
  "system",
  "root",
  "api",
  "www",
] as const;

export type ReservedUsername = (typeof reservedUsernames)[number];
export const RESERVED_USERNAMES: readonly ReservedUsername[] =
  Object.freeze(reservedUsernames);

const reservedUsernameSet: ReadonlySet<string> = new Set(RESERVED_USERNAMES);

export function normalizeUsername(value: string): string {
  return value.toLowerCase();
}

export const UsernameSchema = z
  .string()
  .transform(normalizeUsername)
  .pipe(
    z
      .string()
      .min(USERNAME_MIN_LENGTH)
      .max(USERNAME_MAX_LENGTH)
      .regex(/^[a-z0-9_]+$/u),
  );

export type Username = z.infer<typeof UsernameSchema>;

export function isReservedUsername(username: string): boolean {
  const parsed = UsernameSchema.safeParse(username);
  return parsed.success && reservedUsernameSet.has(parsed.data);
}

export const UsernameAvailabilitySchema = z.discriminatedUnion("status", [
  z.object({
    status: z.literal("available"),
    username: UsernameSchema,
  }),
  z.object({
    status: z.literal("reserved"),
    username: UsernameSchema,
  }),
  z.object({
    status: z.literal("unavailable"),
    username: UsernameSchema,
  }),
]);

export type UsernameAvailability = z.infer<typeof UsernameAvailabilitySchema>;

export const OnboardingSelfDescriptionSchema = z.enum([
  "long_term_investor",
  "active_trader",
  "new_to_investing",
  "hands_off",
  "diversified",
  "concentrated",
]);

export type OnboardingSelfDescription = z.infer<
  typeof OnboardingSelfDescriptionSchema
>;

export const OnboardingInterestSchema = z.enum([
  "blue_chip_stocks",
  "consumer_brands",
  "bonds",
  "crypto",
  "commodities",
  "steady_income",
  "currencies",
  "memecoins",
  "pre_ipo",
  "notable_investor_trackers",
]);

export type OnboardingInterest = z.infer<typeof OnboardingInterestSchema>;

export const ONBOARDING_V1_STEP_IDS = [
  "hook",
  "name",
  "profile",
  "interests",
  "projection",
  "pick",
  "account",
  "fund",
] as const;

export const OnboardingV1StepSchema = z.enum(ONBOARDING_V1_STEP_IDS);
export type OnboardingV1Step = z.infer<typeof OnboardingV1StepSchema>;

export const OnboardingDraftUsernameSchema = UsernameSchema.refine(
  (username) => !reservedUsernameSet.has(username),
  { message: "Username is reserved" },
);

const uniqueAnswerArray = <T extends z.ZodType>(
  schema: T,
  maximum: number,
  minimum = 0,
) =>
  z
    .array(schema)
    .min(minimum)
    .max(maximum)
    .refine((answers) => new Set(answers).size === answers.length, {
      message: "Answers must be unique",
    });

export const OnboardingDraftV1Schema = z
  .object({
    interests: uniqueAnswerArray(OnboardingInterestSchema, 10),
    interestsUnknown: z.boolean(),
    selectedBlueprintId: z.string().trim().min(1).nullable(),
    selectedNetwork: z.literal("EVM"),
    selfDescriptions: uniqueAnswerArray(OnboardingSelfDescriptionSchema, 6),
    updatedAt: z.string().datetime(),
    username: OnboardingDraftUsernameSchema.nullable(),
    version: z.literal(1),
  })
  .strict()
  .superRefine((draft, context) => {
    if (draft.interestsUnknown && draft.interests.length > 0) {
      context.addIssue({
        code: "custom",
        message: "Unknown and concrete interests are mutually exclusive",
        path: ["interestsUnknown"],
      });
    }
  });

export type OnboardingDraftV1 = z.infer<typeof OnboardingDraftV1Schema>;

export const OnboardingRecommendationAnswersSchema = z
  .object({
    interests: uniqueAnswerArray(OnboardingInterestSchema, 10),
    interestsUnknown: z.boolean(),
    selfDescriptions: uniqueAnswerArray(OnboardingSelfDescriptionSchema, 6, 1),
  })
  .strict()
  .superRefine((answers, context) => {
    if (answers.interestsUnknown && answers.interests.length > 0) {
      context.addIssue({
        code: "custom",
        message: "Unknown and concrete interests are mutually exclusive",
        path: ["interestsUnknown"],
      });
    }
    if (!answers.interestsUnknown && answers.interests.length === 0) {
      context.addIssue({
        code: "custom",
        message: "At least one interest answer is required",
        path: ["interests"],
      });
    }
  });

export type OnboardingRecommendationAnswers = z.infer<
  typeof OnboardingRecommendationAnswersSchema
>;

const RISK_SIGNALS: Readonly<
  Record<OnboardingInterest | OnboardingSelfDescription, -1 | 0 | 1>
> = {
  active_trader: 1,
  blue_chip_stocks: -1,
  bonds: -1,
  commodities: 0,
  concentrated: 1,
  consumer_brands: 0,
  crypto: 1,
  currencies: 1,
  diversified: -1,
  hands_off: 0,
  long_term_investor: 0,
  memecoins: 1,
  new_to_investing: -1,
  notable_investor_trackers: 1,
  pre_ipo: 1,
  steady_income: -1,
};

const PRIMARY_GOALS: Readonly<
  Record<OnboardingInterest, Exclude<InvestingPrimaryGoal, "balanced">>
> = {
  blue_chip_stocks: "growth",
  bonds: "preservation",
  commodities: "preservation",
  consumer_brands: "growth",
  crypto: "growth",
  currencies: "exploration",
  memecoins: "exploration",
  notable_investor_trackers: "exploration",
  pre_ipo: "exploration",
  steady_income: "income",
};

export function deriveOnboardingRiskTolerance(
  answers: OnboardingRecommendationAnswers,
): InvestingRiskTolerance {
  const score = scoreOnboardingRiskSignals(answers);

  if (score <= -2) {
    return "conservative";
  }
  if (score >= 2) {
    return "aggressive";
  }
  return "moderate";
}

export function scoreOnboardingRiskSignals(
  answers: OnboardingRecommendationAnswers,
): number {
  return [...answers.selfDescriptions, ...answers.interests].reduce(
    (total, answer) => total + RISK_SIGNALS[answer],
    0,
  );
}

export function deriveOnboardingPrimaryGoal(
  answers: Pick<
    OnboardingRecommendationAnswers,
    "interests" | "interestsUnknown"
  >,
): InvestingPrimaryGoal {
  if (answers.interestsUnknown || answers.interests.length === 0) {
    return "balanced";
  }

  const goals = new Set(
    answers.interests.map((interest) => PRIMARY_GOALS[interest]),
  );
  if (goals.size !== 1) {
    return "balanced";
  }

  return goals.values().next().value ?? "balanced";
}
